import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { SEOHead } from "@/components/SEOHead";
import { FigureTile } from "@/components/figures/FigureTile";
import {
  Figure,
  FigureDomain,
  FigureMode,
  figures,
  modeDefinitions,
} from "@/data/registry";

const domainOrder: { domain: FigureDomain; heading: string }[] = [
  { domain: "rhtp", heading: "Rural Health Transformation Program" },
  { domain: "workforce", heading: "Workforce" },
  { domain: "network", heading: "Insurer directories and network adequacy" },
  { domain: "behavioral", heading: "Behavioral health" },
  { domain: "stroke", heading: "Stroke" },
  { domain: "screening", heading: "Screening and access" },
  { domain: "market", heading: "Market structure" },
];

const modeOrder: FigureMode[] = [
  "MEASURED",
  "DIRECT",
  "FIELD-VERIFIED",
  "MODELED",
];

const ExecutiveDashboardPage = () => {
  const byDomain = (domain: FigureDomain): Figure[] =>
    figures.filter((f) => f.domain === domain);

  return (
    <PublicLayout>
      <SEOHead
        title="State of the System · David Isei"
        description="Hawaiʻi healthcare figures on workforce, network adequacy, behavioral health, stroke, screening, and market structure — each with its source, retrieval date, and mode."
        url="https://davidisei.com/dashboard"
      />

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-liberty-gold">
            State of the System
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-foreground sm:text-5xl">
            What the record shows
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-foreground/80">
            Every figure below carries its source, its retrieval date, and its
            mode. Nothing modeled is presented as measured. When a figure
            changes, the change is logged in the Corrections Log.
          </p>

          <div className="mt-10 rounded-xl border border-border/60 bg-card/40 p-5 sm:p-6">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.16em] text-liberty-gold">
              How to read this page
            </h2>
            <dl className="mt-4 grid gap-4 sm:grid-cols-2">
              {modeOrder.map((mode) => (
                <div key={mode}>
                  <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-foreground">
                    {mode}
                  </dt>
                  <dd className="text-[13px] leading-relaxed text-muted-foreground">
                    {modeDefinitions[mode]}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {domainOrder.map(({ domain, heading }) => {
            const items = byDomain(domain);
            if (items.length === 0) return null;
            return (
              <div key={domain} className="mt-14">
                <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                  {heading}
                </h2>
                <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((figure) => (
                    <FigureTile key={figure.id} figure={figure} />
                  ))}
                </div>
              </div>
            );
          })}

          <p className="mt-16 max-w-3xl border-t border-border/60 pt-6 text-[13px] leading-relaxed text-muted-foreground">
            Figures are drawn from the Task Force claims registry maintained by
            David Isei. Registry counts are ceilings; claims-based counts are
            floors. Ecological analyses describe communities, not individuals.
          </p>

          <Link
            to="/corrections"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-liberty-gold hover:opacity-80"
          >
            Corrections Log <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ExecutiveDashboardPage;
